import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { extractByTag } from "@examspark/common/dist/meta/tags";
import { DisplayName, useQueryGetUnitVocabulary } from "@examspark/common-app";
import { ContainerButton, RichContentDisplay } from "@examspark/common-ui";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { ParsedTCMPCUnit, VocabularyItem } from "@examspark/types";

import ContentSection from "../common/ContentSection";
import UnitContent from "../common/UnitContent";
import VocabularyDetailModal from "../vocabulary/VocabularyDetailModal";

import { Annotations } from "./Annotations";
import UnitVocabularyList from "./vocabulary/UnitVocabularyList";

const StyledVocabulary = styled.div<{ definitions: boolean }>`
  display: flex;

  ${(props) =>
    props.definitions
      ? css`
          flex-direction: column;
          gap: 1.5rem;
        `
      : css`
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        `}
`;

const StyledVocabularyItem = styled.div`
  /* font-size: 1.1rem; */
  line-height: 1.8em;

  *:first-child {
    margin-top: 0;
  }
  *:last-child {
    margin-bottom: 0;
  }

  p {
    display: inline;
  }
`;

export const StyledDefinition = styled(RichContentDisplay)`
  color: #333;
  padding: 0 0.5rem;

  img {
    vertical-align: middle;
  }
  strong {
    /* font-weight: normal; */
  }
  ul,
  li {
    list-style: none;
    padding: 0;
    margin: 1rem 0;
  }

  *:first-child {
    margin-top: 0;
  }
  *:last-child {
    margin-bottom: 0;
  }
`;

export const StyledTerm = styled(ContainerButton)`
  padding: 0.3rem 0.6rem;
  border-radius: 0.5rem;
  padding: 0 0.8rem;
  height: 3rem;
  white-space: nowrap;

  background-color: ${colorFromTheme("surface2")};
  border: solid 1px #ddd;
  /* font-size: 1.1rem; */
  color: #444;
  display: inline-block;
  /* margin-right: 0.4rem; */
  transition: border 0.2s;

  &:hover {
    border: solid 1px #aaa;
  }
`;

function extractVocabularies(
  vocabularies: ParsedTCMPCUnit["vocabularies"],
  section: string,
) {
  return extractByTag(vocabularies, "tcmpc-usage", section);
}

const UnitVocabularySection = (props: {
  unit: ParsedTCMPCUnit;
  vocabularies: VocabularyItem[] | undefined;
  title: string;
  annotationLocationKey: string;
  showDefinitions?: boolean;
  onVocabularyClick: (index: number) => void;
}) => {
  // these MUST be sorted before this component, or the details modal breaks.
  const sortedVocabularies = props.vocabularies ?? [];

  if (sortedVocabularies.length === 0) return null;

  return (
    <ContentSection
      title={props.title}
      actions={
        <Annotations
          resource={props.unit}
          locationKey={props.annotationLocationKey}
        />
      }
    >
      <StyledVocabulary definitions={props.showDefinitions ?? false}>
        {sortedVocabularies.map((vocabulary, index) => (
          <StyledVocabularyItem key={index}>
            <StyledTerm onClick={() => props.onVocabularyClick(index)}>
              <DisplayName resource={vocabulary.vocabulary} />
            </StyledTerm>{" "}
            {props.showDefinitions && (
              <StyledDefinition content={vocabulary.vocabulary?.description} />
            )}
          </StyledVocabularyItem>
        ))}
      </StyledVocabulary>
    </ContentSection>
  );
};

const UnitVocabulary = (props: { unit: ParsedTCMPCUnit }) => {
  const [showVocabularyList, setShowVocabularyList] =
    useState<VocabularyItem[]>();
  const [showVocabularyIndex, setShowVocabularyIndex] = useState<number>();

  const vocabulariesQuery = useQueryGetUnitVocabulary(props.unit);

  const localeSort = (a: VocabularyItem, b: VocabularyItem) =>
    a.vocabulary?.name
      ?.toLocaleLowerCase()
      .localeCompare(b.vocabulary?.name?.toLocaleLowerCase() ?? "") ?? 0;

  const primaryVocabulary = useMemo(
    () =>
      extractVocabularies(vocabulariesQuery.data, "primary").sort(localeSort),
    [vocabulariesQuery.data],
  );
  const relatedVocabulary = useMemo(
    () =>
      extractVocabularies(vocabulariesQuery.data, "related").sort(localeSort),
    [vocabulariesQuery.data],
  );

  return (
    <>
      {props.unit.data?.vocabulary && (
        <ContentSection title="Vocabulary">
          <UnitContent content={props.unit.data?.vocabulary} />
        </ContentSection>
      )}

      <VocabularyDetailModal
        vocabularyList={showVocabularyList ?? []}
        showIndex={showVocabularyIndex}
        onShowIndexChange={setShowVocabularyIndex}
      />

      <UnitVocabularyList
        unit={props.unit}
        vocabularies={primaryVocabulary}
        title="Content Vocabulary"
        annotationLocationKey="content-vocabulary"
        showDefinitions
        onVocabularyClick={(index) => {
          setShowVocabularyList(primaryVocabulary);
          setShowVocabularyIndex(index);
        }}
      />
      <UnitVocabularySection
        unit={props.unit}
        vocabularies={relatedVocabulary}
        title="Related Vocabulary"
        annotationLocationKey="related-vocabulary"
        onVocabularyClick={(index) => {
          setShowVocabularyList(relatedVocabulary);
          setShowVocabularyIndex(index);
        }}
      />
    </>
  );
};

export default UnitVocabulary;
